import { useEffect, useState } from 'react';
import api from '../../../API';
import styles from './JoinCodesPanel.module.css';
import formStyles from '../ProjectEditForm.module.css';

function JoinCodesPanel({ folderId, variant = 'panel' }) {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [role, setRole] = useState('annotator');
    const [customCode, setCustomCode] = useState('');

    async function loadCodes() {
        if (!folderId) return;
        setLoading(true);
        setError('');
        try {
            const r = await api.get(`projects/${folderId}/join/codes`, {});
            if (r.status === 200) {
                setCodes(Array.isArray(r.data?.codes) ? r.data.codes : []);
            } else {
                setError(r.data?.message || 'Failed to load join codes.');
            }
        } catch (e) {
            setError(e?.message || 'Failed to load join codes.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadCodes();
    }, [folderId]);

    async function handleCreate() {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const payload = { role };
            if (customCode.trim()) payload.code = customCode;
            const r = await api.post(`projects/${folderId}/join/codes`, { json: payload });
            if (r.status === 200) {
                const code = r.data?.code?.code;
                setSuccess(code ? `Join code ready: ${code}` : 'Join code created.');
                setCustomCode('');
                await loadCodes();
            } else {
                setError(r.data?.message || 'Failed to create join code.');
            }
        } catch (e) {
            setError(e?.message || 'Failed to create join code.');
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete(codeId) {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const r = await api.delete(`projects/${folderId}/join/codes/${codeId}`, {});
            if (r.status === 200) {
                setSuccess('Join code deleted.');
                await loadCodes();
            } else {
                setError(r.data?.message || 'Failed to delete join code.');
            }
        } catch (e) {
            setError(e?.message || 'Failed to delete join code.');
        } finally {
            setSaving(false);
        }
    }

    async function copyCode(code) {
        try {
            await navigator.clipboard.writeText(code);
            setSuccess('Code copied to clipboard.');
            setError('');
        } catch (e) {
            setError('Unable to copy code.');
        }
    }

    const inner = (
        <>
            {variant !== 'plain' ? (
                <h4 className={formStyles.panelTitle}>Join codes</h4>
            ) : null}
            <p className={styles.help}>
                Create a code for annotator/reviewer access. Share it with users and they can redeem it from the project access gate.
            </p>
            <div className={styles.row}>
                <select
                    className={styles.select}
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={saving}
                >
                    <option value="annotator">Annotator</option>
                    <option value="reviewer">Reviewer</option>
                </select>
                <input
                    type="text"
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value)}
                    placeholder="Optional custom code (leave empty to generate)"
                    className={styles.input}
                    disabled={saving}
                />
                <button type="button" className={styles.btn} onClick={handleCreate} disabled={saving}>
                    {saving ? 'Saving...' : 'Create code'}
                </button>
            </div>
            {error ? <p className={styles.error} role="alert">{error}</p> : null}
            {success ? <p className={styles.success} aria-live="polite">{success}</p> : null}

            {loading ? (
                <p className={formStyles.emptyState}>Loading codes...</p>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Role</th>
                            <th>Uses</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {codes.length === 0 ? (
                            <tr>
                                <td colSpan={4} className={formStyles.emptyState}>No join codes yet.</td>
                            </tr>
                        ) : (
                            codes.map((c) => (
                                <tr key={c.id}>
                                    <td className={styles.code}>{c.code}</td>
                                    <td>{c.role}</td>
                                    <td>{c.uses || 0}</td>
                                    <td>
                                        <div className={styles.actions}>
                                            <button
                                                type="button"
                                                className={styles.btn}
                                                onClick={() => copyCode(c.code)}
                                                disabled={saving}
                                            >
                                                Copy
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.btn}
                                                onClick={() => handleDelete(c.id)}
                                                disabled={saving}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            )}
        </>
    );

    if (variant === 'plain') {
        return <div className={styles.plainRoot}>{inner}</div>;
    }

    return <div className={formStyles.panel}>{inner}</div>;
}

export default JoinCodesPanel;

